import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../api/supabase'
import { motDePasseTemporaire } from '../api/motDePasse'
import {
  droitsEffectifs,
  estDuPersonnel,
  listerCommandes,
  listerDroitsParRole,
  monStaff,
  suivreCommandes,
  type CleDroit,
  type Droits,
  type MonStaff,
} from '../api/dispatch'
import type { Commande } from '../api/types'
import { STATUTS_ACTIFS } from '../api/types'
import { apercuCommandes } from '../api/apercu'
import { Connexion } from './Connexion'
import { creerCompte } from '../api/creerCompte'
import { controlerPersonnel } from '../api/controleAcces'
import { erreurDeRetourOAuth, sessionOuverteParGoogle } from '../api/google'
import { BarreLaterale } from './BarreLaterale'
import { MotDePasse } from './MotDePasse'
import { EcranLive } from './ecrans/Live'
import { EcranCommandes } from './ecrans/Commandes'
import { EcranLivreurs } from './ecrans/Livreurs'
import { EcranMarchands } from './ecrans/Marchands'
import { EcranCandidatures } from './ecrans/Candidatures'
import { EcranContacts } from './ecrans/Contacts'
import { EcranChat } from './ecrans/Chat'
import { cleFil, type Casquette } from '../api/discussions'
import { EcranPaiements } from './ecrans/Paiements'
import { EcranPaie } from './ecrans/Paie'
import { EcranReversements } from './ecrans/Reversements'
import { EcranConfig } from './ecrans/Config'
import { EcranEquipe } from './ecrans/Equipe'
import { EcranCompta } from './ecrans/Compta'
import { EcranCguClients } from './ecrans/CguClients'
import { EcranAbonnesPremium } from './ecrans/AbonnesPremium'

export type Onglet =
  | 'live' | 'commandes' | 'chat' | 'contacts' | 'livreurs' | 'marchands' | 'candidatures'
  | 'paiements' | 'premium' | 'paie' | 'reversements' | 'compta' | 'config' | 'equipe' | 'cgu'

const ONGLETS: Array<{ cle: Onglet; libelle: string }> = [
  { cle: 'live', libelle: 'Live' },
  { cle: 'commandes', libelle: 'Commandes' },
  { cle: 'chat', libelle: 'Chat' },
  { cle: 'contacts', libelle: 'Contacts' },
  { cle: 'livreurs', libelle: 'Livreurs' },
  { cle: 'marchands', libelle: 'Marchands' },
  { cle: 'candidatures', libelle: 'Candidatures' },
  { cle: 'paiements', libelle: 'Paiements' },
  { cle: 'premium', libelle: 'Abonnés Premium' },
  { cle: 'paie', libelle: 'Paie livreurs' },
  { cle: 'reversements', libelle: 'Reversements marchands' },
  { cle: 'compta', libelle: 'Comptabilité' },
  { cle: 'config', libelle: 'Config' },
  { cle: 'equipe', libelle: 'Équipe' },
  { cle: 'cgu', libelle: 'CGU clients' },
]

/**
 * Le droit exigé par chaque onglet — repris tel quel de l'original.
 *
 * L'écran précédent ne connaissait qu'un booléen `estAdmin` : un « support »
 * voyait donc la comptabilité et la paie, et un « comptable » se voyait refuser
 * des onglets auxquels il a droit. Les droits sont fins et viennent de
 * `staff_role_permissions`, la table même que lit `has_perm()` derrière la RLS.
 *
 * ⚠️ Ce masquage reste du CONFORT, pas de la sécurité : la vraie barrière est
 * la RLS. Il évite surtout d'ouvrir des écrans qui ne rendraient que du vide.
 */
const DROIT_ONGLET: Record<Onglet, CleDroit> = {
  live: 'voir_live',
  commandes: 'gerer_courses',
  chat: 'voir_live',
  contacts: 'voir_live',
  livreurs: 'gerer_livreurs',
  marchands: 'gerer_marchands',
  candidatures: 'gerer_livreurs',
  paiements: 'voir_paiements',
  premium: 'voir_paiements',
  paie: 'gerer_paie',
  reversements: 'gerer_paie',
  compta: 'voir_compta',
  config: 'gerer_config',
  equipe: 'gerer_equipe',
  /* Réservé aux ADMINS (voir `RESERVE_ADMIN`) : ce droit n'est qu'un repli si le rôle est illisible. */
  cgu: 'gerer_config',
}

/**
 * Onglets réservés au rôle `admin` quoi que disent les droits fins : les CGU engagent la société (texte juridique,
 * preuve d'acceptation). La base applique la même règle (`is_admin()`), ce masquage évite seulement un écran vide.
 */
const RESERVE_ADMIN: ReadonlySet<Onglet> = new Set<Onglet>(['cgu'])

export function App() {
  const [session, setSession] = useState<'chargement' | 'dehors' | 'dedans'>('chargement')
  const [moi, setMoi] = useState<MonStaff>({ profileId: null, role: null, permissions: {} })
  const [mesDroits, setMesDroits] = useState<Droits>({})
  const [onglet, setOnglet] = useState<Onglet>('live')
  // Le repli de la barre latérale existait dans la console d'origine
  // (`.sidebar.collapsed` + bouton `.side-toggle`) : sur un écran de dispatch,
  // récupérer 150 px de largeur pour la carte n'est pas un détail.
  const [replie, setReplie] = useState(false)
  const [commandes, setCommandes] = useState<Commande[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  /* Compte né avec un mot de passe temporaire : la console invite à le changer (on peut reporter ; l'invitation revient à la connexion suivante). */
  const [inviterMdp, setInviterMdp] = useState(false)
  /* Aperçu seulement : les écritures fictives modifient `apercuCommandes()` en place ; ce compteur redessine. */
  const [, setTickApercu] = useState(0)
  /* Le pair à ouvrir dans la messagerie quand on arrive depuis un bouton
     « Message ». Sans lui, le bouton ouvrait l'onglet et laissait choisir
     l'interlocuteur à la main — le geste utile était perdu en route. */
  const [pairInitial, setPairInitial] = useState<string | null>(null)
  /* La commande dont on ouvre le fil client ↔ livreur (lien de sa fiche). */
  const [discussionInitiale, setDiscussionInitiale] = useState<string | null>(null)

  const chargerCommandes = useCallback(async () => {
    try {
      setCommandes(await listerCommandes())
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Chargement des commandes impossible.')
    }
  }, [])

  const entrer = useCallback(async (): Promise<string | null> => {
    /* Contrôle d'accès, AVANT d'ouvrir quoi que ce soit : cette console est
       réservée au personnel. Vaut pour le mot de passe comme pour Google (qui
       ne donne aucun rôle) : un compte sans accès est déconnecté, avec un
       message clair. */
    const verdict = await controlerPersonnel(await sessionOuverteParGoogle(), {
      estDuPersonnel,
      deconnecter: async () => {
        await supabase.auth.signOut()
      },
    })
    if (!verdict.ok) {
      setErreur(verdict.message)
      setSession('dehors')
      return verdict.message
    }
    try {
      const [fiche, parRole] = await Promise.all([monStaff(), listerDroitsParRole()])
      setMoi(fiche)
      setMesDroits(droitsEffectifs(fiche, parRole))
    } catch (e) {
      /* Droits illisibles : on laisse la barre COMPLÈTE plutôt que de tout
         verrouiller. Un incident de lecture ne doit pas enfermer dehors le
         dispatch de garde à deux heures du matin — la RLS, elle, tient. */
      console.warn('Droits illisibles :', e)
      setMesDroits({})
    }
    try {
      const { data } = await supabase.auth.getSession()
      setInviterMdp(motDePasseTemporaire(data.session?.user))
    } catch {
      setInviterMdp(false)
    }
    setSession('dedans')
    await chargerCommandes()
    return null
  }, [chargerCommandes])

  useEffect(() => {
    let vivant = true
    void supabase.auth.getSession().then(({ data }) => {
      if (!vivant) return
      if (data.session) void entrer()
      else {
        /* Retour d'OAuth refusé (annulé chez Google…) : on le dit. */
        const retour = erreurDeRetourOAuth()
        if (retour) setErreur(retour)
        setSession('dehors')
      }
    })
    return () => { vivant = false }
  }, [entrer])

  useEffect(() => {
    if (session !== 'dedans') return
    return suivreCommandes(() => void chargerCommandes())
  }, [session, chargerCommandes])

  /* APERÇU — outil de développement, jamais livré.
     `import.meta.env.DEV` vaut `false` au build : le bloc entier disparaît du
     bundle de production. Il sert à comparer écran par écran avec la console
     HTML SANS se connecter. Sans lui, la seule façon de voir une régression
     visuelle sur le Dispatch était de la découvrir en production. */
  if (import.meta.env.DEV && new URLSearchParams(location.search).has('apercu')) {
    return (
      <Coquille
        onglet={onglet}
        surOnglet={setOnglet}
        visibles={ONGLETS}
        commandes={apercuCommandes()}
        moi={{ profileId: null, role: 'admin', permissions: {} }}
        pairInitial={null}
        discussionInitiale={null}
        surContacter={() => undefined}
        surVoirDiscussion={() => undefined}
        erreur={null}
        replie={replie}
        surReplier={() => setReplie((r) => !r)}
        surDeconnexion={() => undefined}
        invitationMotDePasse={false}
        chargerCommandes={async () => setTickApercu((t) => t + 1)}
      />
    )
  }

  if (session === 'chargement') return <div className="login-wrap" />

  if (session === 'dehors') {
    return (
      <Connexion
        erreurInitiale={erreur}
        surConnexion={async (email, motDePasse) => {
          const { error } = await supabase.auth.signInWithPassword({ email, password: motDePasse })
          if (error) throw new Error('E-mail ou mot de passe incorrect.')
          /* `entrer` renvoie le motif d'un refus d'accès : on le lève pour que le
             formulaire, déjà affiché, le montre. */
          const refus = await entrer()
          if (refus) throw new Error(refus)
        }}
        surInscription={async (nom, email, motDePasse) => {
          const r = await creerCompte(nom, email, motDePasse)
          /* Confirmation par e-mail désactivée : une session est déjà ouverte, le
             contrôle d'accès habituel s'applique (compte sans droits = refusé). */
          if (r.sessionOuverte) {
            const refus = await entrer()
            if (refus) throw new Error(refus)
          }
          return r
        }}
        /* Code WhatsApp vérifié : MÊME chemin que le mot de passe (contrôle d'accès au personnel, droits, commandes). */
        surConnexionWhatsapp={entrer}
      />
    )
  }

  /* Aucun droit résolu = configuration muette, pas un compte sans droits :
     on n'ampute pas la barre latérale sur une lecture qui a échoué. */
  const droitsConnus = Object.values(mesDroits).some(Boolean)
  const visibles = ONGLETS.filter(
    (o) => (!droitsConnus || mesDroits[DROIT_ONGLET[o.cle]]) && (!RESERVE_ADMIN.has(o.cle) || moi.role === 'admin'),
  )
  const courant: Onglet = visibles.some((o) => o.cle === onglet) ? onglet : 'live'
  return (
    <Coquille
      onglet={courant}
      surOnglet={setOnglet}
      visibles={visibles}
      commandes={commandes}
      moi={moi}
      pairInitial={pairInitial}
      discussionInitiale={discussionInitiale}
      surContacter={(id, casquette) => {
        setPairInitial(cleFil(id, casquette))
        setDiscussionInitiale(null)
        setOnglet('chat')
      }}
      surVoirDiscussion={(commandeId) => {
        setDiscussionInitiale(commandeId)
        setPairInitial(null)
        setOnglet('chat')
      }}
      erreur={erreur}
      replie={replie}
      surReplier={() => setReplie((r) => !r)}
      surDeconnexion={() => void supabase.auth.signOut().then(() => { setInviterMdp(false); setSession('dehors') })}
      invitationMotDePasse={inviterMdp}
      chargerCommandes={chargerCommandes}
    />
  )
}

/**
 * La coquille — barre latérale, en-tête, écran courant.
 *
 * Extraite pour que le mode aperçu la rende SANS session : c'est la seule
 * façon de comparer les écrans au gabarit HTML sans identifiants.
 */
function Coquille({
  onglet,
  surOnglet,
  visibles,
  commandes,
  moi,
  pairInitial,
  discussionInitiale,
  surContacter,
  surVoirDiscussion,
  erreur,
  replie,
  surReplier,
  surDeconnexion,
  invitationMotDePasse,
  chargerCommandes,
}: {
  onglet: Onglet
  surOnglet: (o: Onglet) => void
  visibles: typeof ONGLETS
  commandes: Commande[]
  moi: MonStaff
  pairInitial: string | null
  discussionInitiale: string | null
  surContacter: (id: string, casquette: Casquette) => void
  surVoirDiscussion: (commandeId: string) => void
  erreur: string | null
  replie: boolean
  surReplier: () => void
  surDeconnexion: () => void
  /** Le compte a un mot de passe temporaire : la fenêtre « Changer mon mot de passe » s'ouvre d'office. */
  invitationMotDePasse: boolean
  chargerCommandes: () => Promise<void>
}) {
  const courant = onglet
  const [motDePasse, setMotDePasse] = useState(invitationMotDePasse)
  /* Rejoué à chaque ouverture de session : la fenêtre ne se rouvre pas d'elle-même
     après « Plus tard » tant qu'on reste connecté, mais revient à la connexion suivante. */
  useEffect(() => {
    if (invitationMotDePasse) setMotDePasse(true)
  }, [invitationMotDePasse])
  const actives = commandes.filter((c) => STATUTS_ACTIFS.includes(c.statut))
  return (

    /* ⚠️ `app active`, et SURTOUT pas `display:flex`.
       Les deux consoles n'ont pas la même mise en page et j'avais appliqué
       ici celle du Marchand. Le Dispatch pose sa barre latérale en
       `position:fixed` et décale le contenu par `margin-left:230px` ; le
       Marchand, lui, est un vrai conteneur flex avec `.content{flex:1}`.
       En forçant `display:flex` ici, `.content` devenait un élément flex sans
       `flex:1` : il se dimensionnait sur son CONTENU et laissait 200 px de
       vide à droite. La classe `active` est ce qui rend la coquille visible
       (`.app{display:none}`). */
    <div className="app active">
      {motDePasse && <MotDePasse invitation={invitationMotDePasse} surFermer={() => setMotDePasse(false)} />}
      <BarreLaterale
        actif={courant}
        autorise={(o) => visibles.some((v) => v.cle === o)}
        nbActives={actives.length}
        replie={replie}
        surChangement={surOnglet}
        surReplier={surReplier}
        surMotDePasse={() => setMotDePasse(true)}
        surDeconnexion={surDeconnexion}
      />

      <div className="content">
        <div className="header">
          <h2>{ONGLETS.find((o) => o.cle === courant)?.libelle}</h2>
          <div className="spacer" style={{ flex: 1 }} />
          {erreur && <span style={{ fontSize: 12, color: 'var(--rouge)', fontWeight: 600 }}>{erreur}</span>}
        </div>
        {/* ⚠️ `section.active` porte les animations d'entrée : les tuiles qui
            montent en décalé et les cartes. Le sélecteur vise l'ÉLÉMENT
            `section`, pas une classe — un `<div>` ne déclencherait rien. La
            `key` relance l'animation à chaque changement d'onglet, comme le
            faisait l'ajout de `.active` dans l'original. */}
        <div className="main">
          <section className="active" key={courant}>
          {courant === 'live' && (
            <EcranLive
              commandes={commandes}
              surRecharger={chargerCommandes}
              surContacter={surContacter}
              surVoirDiscussion={surVoirDiscussion}
            />
          )}
          {courant === 'commandes' && <EcranCommandes commandes={commandes} />}
          {courant === 'chat' && <EcranChat pairInitial={pairInitial} discussionInitiale={discussionInitiale} />}
          {/* Les fiches marchand et livreur vivent dans LEURS écrans : cliquer
              un contact y renvoie plutôt que de dupliquer deux panneaux. */}
          {courant === 'contacts' && (
            <EcranContacts
              commandes={commandes}
              surContacter={surContacter}
              surMarchand={() => surOnglet('marchands')}
              surLivreur={() => surOnglet('livreurs')}
            />
          )}
          {courant === 'livreurs' && (
            <EcranLivreurs
              surContacter={surContacter}
            />
          )}
          {courant === 'marchands' && (
            <EcranMarchands
              surContacter={surContacter}
            />
          )}
          {courant === 'candidatures' && <EcranCandidatures estAdmin={moi.role === 'admin'} />}
          {courant === 'paiements' && <EcranPaiements />}
          {courant === 'premium' && <EcranAbonnesPremium estAdmin={moi.role === 'admin'} />}
          {courant === 'paie' && <EcranPaie />}
          {courant === 'reversements' && <EcranReversements estAdmin={moi.role === 'admin'} />}
          {courant === 'compta' && <EcranCompta commandes={commandes} />}
          {courant === 'config' && <EcranConfig />}
          {courant === 'equipe' && <EcranEquipe monProfileId={moi.profileId} />}
          {courant === 'cgu' && moi.role === 'admin' && <EcranCguClients />}
          </section>
        </div>
      </div>
    </div>
    )
}
